import React from "react";
import { associationDeliversReceipt } from "../utils/cerfa";
import { Link } from "react-router-dom";
import { CheckIcon } from "./Icons";
import { getAvatarColor, getInitials } from "./UserAvatar";
import { slugify } from "../utils/slugify";

// Cover pastel rotatif quand l'asso n'a pas de photo de couverture :
// une couleur douce et DIFFÉRENTE par association (au lieu d'un dégradé
// bleu-violet unique qui rendait la page des associations monotone/fade).
// Classes en toutes lettres pour ne pas être purgées par Tailwind.
const PASTEL_COVERS = [
  "from-blue-100 to-violet-100",
  "from-rose-100 to-orange-100",
  "from-emerald-100 to-teal-100",
  "from-amber-100 to-yellow-100",
  "from-sky-100 to-indigo-100",
  "from-fuchsia-100 to-pink-100",
  "from-lime-100 to-emerald-100",
  "from-cyan-100 to-sky-100",
  "from-violet-100 to-fuchsia-100",
  "from-teal-100 to-cyan-100",
];
export const pastelCover = (id) => {
  let h = 0;
  const s = String(id || "");
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PASTEL_COVERS[h % PASTEL_COVERS.length];
};

const AssociationCard = ({ association }) => {

  return (
    <Link
      to={`/association/${slugify(association.name, association.association_id)}`}
      className="block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all border border-slate-100 group"
      data-testid={`association-card-${association.association_id}`}
    >
      {/* Cover Image — dégradé pastel rotatif quand pas de couverture
          (varié par association, fini l'effet monotone). Phase 13.70 —
          overflow-hidden retiré de ce conteneur (clippait le logo). */}
      <div className={`relative h-24 sm:h-28 bg-gradient-to-br ${pastelCover(association.association_id)}`}>
        {/* Wrapper qui clippe l'image (et son hover scale-105) sans
            toucher au logo qui depasse. */}
        <div className="absolute inset-0 overflow-hidden rounded-t-2xl">
          {association.cover_image && (
            <img
              src={association.cover_image}
              alt={`Couverture ${association.name}`}
              loading="lazy"
              decoding="async"
              width="400"
              height="128"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          )}
          {association.cover_image && (
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          )}
        </div>

        {/* Logo overlay — À L'INTÉRIEUR de la couverture (décision Lucas
            06/08 : « la photo de garde doit descendre au même niveau que la
            partie basse de la photo de profil, comme sur Facebook »). Avant,
            le logo débordait à moitié sous la couverture qui le coupait au
            milieu. Pur CSS : aucune photo à refaire pour les assos inscrites,
            et même visuel partout (liste publique, aperçu du tunnel). */}
        <div className="absolute bottom-3 left-4 z-10">
          {association.logo ? (
            <img
              src={association.logo}
              alt={association.name}
              loading="lazy"
              decoding="async"
              width="64"
              height="64"
              className="w-14 h-14 rounded-xl object-cover bg-white border-[3px] border-white shadow-lg"
            />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-white border-[3px] border-white shadow-lg flex items-center justify-center font-bold text-lg text-white"
              style={{ backgroundColor: getAvatarColor(association.name) }}>
              {getInitials(association.name)}
            </div>
          )}
        </div>

        {/* Verified badge */}
        {association.is_verified && (
          <div className="absolute top-3 right-3 z-10 bg-white/90 text-green-600 text-xs px-2 py-1 rounded-full flex items-center gap-1 font-medium">
            <CheckIcon className="w-3 h-3" />
            Vérifiée
          </div>
        )}

        {/* Fondatrice/Ambassadrice : DÉPLACÉS dans la rangée de pastilles sous
            la description (décision Lucas 06/08) — en haut à gauche de la
            couverture, ils chevauchaient le logo depuis le passage façon
            Facebook. La rangée flex-wrap ne peut jamais rien chevaucher. */}
      </div>
      
      {/* Content — Phase 13.30 : refonte pour grand public. La carte
          raconte 4 choses dans cet ordre :
          1) QUI : nom + ville
          2) QUOI : courte description (mission de l'asso, ~2 lignes)
          3) DOMAINE : tag de type (Culture, Sport, etc.)
          4) IMPACT : nb contributions (preuve sociale, pas de fiscalité) */}
      <div className="pt-3 pb-3.5 px-3.5">
        <h3 className="font-semibold text-slate-900 mb-1 line-clamp-1">{association.name}</h3>
        <p className="text-sm text-slate-500 mb-2 flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {association.city}
        </p>

        {/* Mission courte — humanise la carte. Si pas de description,
            on ne montre rien (pas de placeholder pour eviter le bruit). */}
        {association.description && (
          <p className="text-[13px] text-slate-600 leading-snug line-clamp-2 mb-3">
            {association.description}
          </p>
        )}

        {/* Phase 13.33 — Tags normalises (alignes avec les 3 parcours :
            page recherche, step 6 publish, page detail). On affiche :
            1. Type d'asso (slate, neutre)
            2. "Recu fiscal" (vert/violet selon fiscal_type) si CERFA delivre
               — SANS le %, le % apparait dans le calcul detaille uniquement
            3. "Utilite publique" (amber pale) si is_arup=true
            4. "Paiements en cours" (slate) si payment_status pending/not_configured */}
        <div className="flex flex-wrap gap-1 mb-3">
          {/* Phase 13.2/13.3 — badges d'honneur, désormais en pastilles. */}
          {association.is_founding_partner && (
            <span
              className="inline-flex items-center gap-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs px-2 py-0.5 rounded-full font-bold"
              title="Association Fondatrice — l'une des premières assos de Gively"
            >
              ⭐ Fondatrice
            </span>
          )}
          {!association.is_founding_partner && association.is_ambassador && (
            <span
              className="inline-flex items-center gap-1 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-xs px-2 py-0.5 rounded-full font-bold"
              title="Association Ambassadrice — a parrainé 3 autres associations sur Gively"
            >
              🤝 Ambassadrice
            </span>
          )}
          {association.type && (
            <span className="inline-block bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full">
              {association.type}
            </span>
          )}
          {associationDeliversReceipt(association) && (
            <span
              className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${
                association.fiscal_type === "aide_personnes_difficulte"
                  ? "bg-purple-100 text-purple-700"
                  : "bg-emerald-100 text-emerald-700"
              }`}
              title="Cette association est habilitée à délivrer des reçus fiscaux pour les dons qu'elle reçoit. L'éligibilité à une réduction d'impôt dépend de votre situation et du type de versement."
            >
              ✓ Réduction d'impôt
            </span>
          )}
          {association.is_arup && (
            <span
              className="inline-block bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full font-medium"
              title="Reconnue d'utilité publique — statut juridique premium accordé par décret en Conseil d'État"
            >
              🏛️ Utilité publique
            </span>
          )}
          {/* Phase 13.35 — Badge "Rescrit fiscal" : signal premium fort.
              Le rescrit est une confirmation ECRITE de l'administration
              fiscale sur l'eligibilite de l'asso. Impossible a falsifier.
              On affiche le badge meme sans CERFA car le rescrit prouve
              juste l'eligibilite (theoriquement, l'asso pourrait choisir
              de ne pas delivrer le CERFA tout en ayant le rescrit). */}
          {association.has_rescrit_fiscal && (
            <span
              className="inline-block bg-emerald-100 text-emerald-800 text-xs px-2 py-0.5 rounded-full font-medium border border-emerald-300"
              title="Rescrit fiscal validé — confirmation officielle de l'administration fiscale sur l'éligibilité fiscale de l'association"
            >
              🔒 Rescrit fiscal
            </span>
          )}
          {(association.payment_status === "pending" || association.payment_status === "not_configured") && (
            <span
              className="inline-block bg-slate-100 text-slate-500 text-xs px-2 py-0.5 rounded-full font-medium"
              title="Cette association est en train de finaliser la configuration de ses paiements."
            >
              Paiements en cours
            </span>
          )}
        </div>

        {/* Stats — preuve sociale */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <p className="text-sm font-semibold text-[#1DA1F2]">
            {association.total_donations_count || 0} contribution{(association.total_donations_count || 0) !== 1 ? 's' : ''}
          </p>
          {association.followers_count > 0 && (
            <p className="text-xs text-slate-400">
              {association.followers_count} abonné{association.followers_count !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      </div>
    </Link>
  );
};

// Skeleton Components

export { AssociationCard };
